import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, MapPin, Newspaper, ExternalLink, CloudLightning, Sparkles } from "lucide-react";
import { TYPE_LABELS } from "@/pages/Disasters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

/** /places/:slug — "More about <place>": the AI-written place page plus the news that named it. */
export default function PlacePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/v1/places/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <main className="pt-48 pb-32 text-center text-[color:var(--ink-soft)]">Reading about this place…</main>;
  if (!data) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-48 pb-32 text-center" data-testid="place-not-found">
        <h1 className="heritage-title text-4xl">No page for this place yet.</h1>
        <p className="mt-3 text-[color:var(--ink-soft)]">
          Place pages are written when a place appears in the disaster wire, or when a member asks for one.
        </p>
        <Link to="/news" className="btn-ghost mt-6 inline-flex" data-testid="place-back-wire">
          <ArrowLeft size={14} /> The Wire
        </Link>
      </main>
    );
  }

  const { place, news = [], disasters = [] } = data;

  return (
    <main data-testid="place-page" className="pt-40 md:pt-48">
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16">
        <Link to="/news" className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-[color:var(--ink-soft)] link-underline mb-8" data-testid="place-back">
          <ArrowLeft size={12} /> <span>The Wire</span>
        </Link>
        <div className="overline text-[color:var(--ink-soft)] mb-4 flex items-center gap-2">
          <MapPin size={12} className="text-[color:var(--rust)]" /> § More about
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="font-serif-display leading-[0.95]"
          style={{ fontSize: "clamp(48px, 8vw, 110px)" }}
          data-testid="place-name"
        >
          {place.place}
          {place.country && <span className="font-italic-serif italic text-[color:var(--ink-soft)]">, {place.country}</span>}
        </motion.h1>
        <div className="mt-6 numeric-index text-[color:var(--ink-soft)] flex items-center gap-1.5">
          <Sparkles size={11} /> Written by our AI correspondent · kept under human review
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16" data-testid="place-article">
        <div className="rule-strong mb-8" />
        {place.article.split(/\n+/).filter(Boolean).map((para, i) => (
          <p key={i} className="text-[17px] leading-[1.8] mb-5 text-[color:var(--ink)]">{para}</p>
        ))}
      </section>

      {disasters.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-10 pb-14" data-testid="place-disasters">
          <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
            <CloudLightning size={12} /> Disasters touching this place
          </div>
          <div className="space-y-3">
            {disasters.map((d) => (
              <Link
                key={d.id}
                to={`/disasters/${d.id}`}
                data-testid={`place-disaster-${d.id}`}
                className="group flex items-center justify-between border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] px-5 py-4"
              >
                <span className="font-serif-display text-lg group-hover:text-[color:var(--rust)] transition-colors">{d.name}</span>
                <span className="numeric-index text-[color:var(--ink-soft)]">{TYPE_LABELS[d.type] || d.type} · {d.status}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {news.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24" data-testid="place-news">
          <div className="overline text-[color:var(--sepia)] mb-4 flex items-center gap-2">
            <Newspaper size={12} /> In the news
          </div>
          <ol className="divide-y divide-[color:var(--sepia)]/15">
            {news.map((n, i) => (
              <li key={i} className="py-3" data-testid={`place-news-${i}`}>
                <a href={n.link} target="_blank" rel="noopener noreferrer" className="group flex items-baseline gap-2">
                  <span className="font-serif-display text-[16px] leading-snug group-hover:text-[color:var(--rust)] transition-colors">{n.title}</span>
                  <ExternalLink size={11} className="shrink-0 text-[color:var(--sepia)]/50" />
                </a>
                <div className="mt-0.5 numeric-index text-[color:var(--ink-soft)]">{n.source}</div>
              </li>
            ))}
          </ol>
        </section>
      )}
    </main>
  );
}
